import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bike, Car, Truck, MapPin, Phone, IdCard, Hash, User as UserIcon, Calendar, Loader2, Send } from 'lucide-react';
import { driverApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import FileUpload from '../components/FileUpload';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { toast } from 'sonner';

const VEHICLES = [
  { v: 'Bike', icon: Bike }, { v: 'Scooter', icon: Bike }, { v: 'Car', icon: Car }, { v: 'Van', icon: Truck },
];

const DriverOnboarding = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [existing, setExisting] = useState(null);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: '', dob: '', phone: '', home_address: '', home_address_lat: null, home_address_lng: null,
    service_area: 'Ottawa', vehicle_type: 'Bike', vehicle_plate: '', license_number: '',
    photo: '', license_photo_url: '', license_photo_back_url: '', registration_photo_url: '', insurance_doc_url: '',
    lat: null, lng: null,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login'); return; }
    setForm((f) => ({ ...f, full_name: user.name || '' }));
    driverApi.me().then((d) => setExisting(d)).catch(() => setExisting(null)).finally(() => setChecking(false));
  }, [loading, user, nav]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude })),
      () => {},
      { timeout: 8000 },
    );
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        lat: form.lat ?? 45.4215, lng: form.lng ?? -75.6972,
      };
      const d = await driverApi.apply(payload);
      toast.success('Application submitted! Awaiting admin approval.');
      setExisting(d);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (existing) {
    const map = {
      pending: { color: 'bg-amber-50 text-amber-700 border-amber-200', msg: 'Your application is under review. We’ll notify you once approved.' },
      approved: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', msg: 'Your driver application is approved!' },
      rejected: { color: 'bg-rose-50 text-rose-700 border-rose-200', msg: 'Your application was rejected. Contact support.' },
    };
    const s = map[existing.status] || map.pending;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/30">
            <Send className="h-7 w-7 text-white -rotate-12" strokeWidth={2.3} />
          </div>
          <h1 className="mt-5 text-2xl font-semibold text-slate-900">Driver application</h1>
          <div className={`mt-3 inline-block px-3 py-1.5 rounded-md text-sm font-medium border ${s.color} capitalize`}>{existing.status}</div>
          <p className="mt-3 text-sm text-slate-600">{s.msg}</p>
          <div className="mt-6 flex gap-2 justify-center">
            {existing.status === 'approved' ? (
              <Button onClick={() => nav('/driver')} className="bg-emerald-600 hover:bg-emerald-700 text-white">Go to driver portal</Button>
            ) : (
              <Button variant="outline" onClick={() => nav('/')}>Back to app</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DOB age helpers — drivers must be at least 18
  const today = new Date();
  const max18 = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().slice(0, 10);
  const dobAgeOk = (() => {
    if (!form.dob) return false;
    const d = new Date(form.dob);
    if (Number.isNaN(d.getTime())) return false;
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age >= 18;
  })();

  const canNext1 = form.full_name && dobAgeOk && form.phone && form.home_address;
  const canNext2 = form.vehicle_type && form.vehicle_plate && form.license_number;
  const canSubmit = canNext1 && canNext2 && form.photo && form.license_photo_url;

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-10">
      <div className="max-w-3xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-600 flex items-center justify-center">
            <Send className="h-6 w-6 text-white -rotate-12" strokeWidth={2.3} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Become a ShipitNow driver</h1>
            <p className="text-slate-500 text-sm">Step {step} of 3 — takes about 3 minutes</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-[12px] text-emerald-900 leading-snug" data-testid="driver-onboarding-mission">
          ShipItNow uses its <strong>registered and approved drivers</strong> to deliver food, same-day delivery, courier services, and business logistics in <strong>Canada and Ibadan</strong>. Complete this form to join our roster.
        </div>

        <div className="mt-5 flex items-center gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1.5 rounded-full ${n <= step ? 'bg-emerald-600' : 'bg-slate-200'}`} />
          ))}
        </div>

        {step === 1 && (
          <section className="mt-6 space-y-4">
            <h2 className="font-semibold text-slate-900">Personal details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={UserIcon} label="Full legal name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
              <div>
                <Field icon={Calendar} label="Date of birth" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} max={max18} />
                {form.dob && !dobAgeOk && (
                  <p className="text-xs text-rose-600 mt-1" data-testid="dob-age-error">You must be at least 18 years old to drive for ShipitNow.</p>
                )}
              </div>
              <Field icon={Phone} label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field icon={MapPin} label="Service area" value={form.service_area} onChange={(v) => setForm({ ...form, service_area: v })} />
            </div>
            <AddressAutocomplete
              label="Home address"
              value={form.home_address}
              onChange={(addr, geo) => setForm((f) => ({ ...f, home_address: addr, home_address_lat: geo?.lat ?? f.home_address_lat, home_address_lng: geo?.lng ?? f.home_address_lng }))}
            />
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canNext1} className="bg-slate-900 hover:bg-slate-800 text-white">Continue →</Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="mt-6 space-y-4">
            <h2 className="font-semibold text-slate-900">Vehicle & license</h2>
            <div>
              <Label className="text-xs text-slate-600">Vehicle type</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {VEHICLES.map((v) => (
                  <button type="button" key={v.v} onClick={() => setForm({ ...form, vehicle_type: v.v })}
                    className={`h-12 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium ${form.vehicle_type === v.v ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}>
                    <v.icon className="h-4 w-4" />{v.v}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={Hash} label="Vehicle plate" value={form.vehicle_plate} onChange={(v) => setForm({ ...form, vehicle_plate: v })} placeholder="XB-902" />
              <Field icon={IdCard} label="License number" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} />
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
              <Button onClick={() => setStep(3)} disabled={!canNext2} className="bg-slate-900 hover:bg-slate-800 text-white">Continue →</Button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="mt-6 space-y-4">
            <h2 className="font-semibold text-slate-900">Documents & photos</h2>
            <p className="text-sm text-slate-500">Use your phone’s camera or upload from gallery. Max 5 MB each.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FileUpload label="Selfie / profile photo *" capture accept="image/*" placeholder="Tap to take a selfie"
                value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v?.url || '' }))} />
              <FileUpload label="Driver license (front) *" capture accept="image/*" placeholder="Capture license front"
                value={form.license_photo_url} onChange={(v) => setForm((f) => ({ ...f, license_photo_url: v?.url || '' }))} />
              <FileUpload label="Driver license (back)" capture accept="image/*" placeholder="Capture license back"
                value={form.license_photo_back_url} onChange={(v) => setForm((f) => ({ ...f, license_photo_back_url: v?.url || '' }))} />
              <FileUpload label="Vehicle registration" capture accept="image/*" placeholder="Capture registration"
                value={form.registration_photo_url} onChange={(v) => setForm((f) => ({ ...f, registration_photo_url: v?.url || '' }))} />
              <FileUpload label="Insurance document (PDF / image)" accept="image/*,application/pdf" placeholder="Upload insurance"
                value={form.insurance_doc_url} onChange={(v) => setForm((f) => ({ ...f, insurance_doc_url: v?.url || '' }))} />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
              <Button onClick={submit} disabled={submitting || !canSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit application
              </Button>
            </div>
            {!canSubmit && <p className="text-xs text-slate-400 text-right">* Selfie & license front are required</p>}
          </section>
        )}
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, type = 'text', placeholder, max }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative mt-1">
      {Icon && <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      <Input type={type} max={max} className={Icon ? 'pl-9 h-10' : 'h-10'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  </div>
);

export default DriverOnboarding;
